import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Users,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  Pencil,
  ExternalLink,
} from 'lucide-react';

import { useCMSDetailQuery } from '@/client/api/cms/cms.query';
import { CMSObject, deriveCMSStatus, publicContentUrl } from '@/client/types/cms';
import CMSMediaSlider from './preview/CMSMediaSlider';
import { decodeHtmlEntities, decodeHtmlEntitiesArray } from './preview/htmlText';
import './preview/content-preview.css';

// ── Helpers (ported from ff-ui pages/Content/ContentPage) ────────────────────

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Estimate reading time at ~220 wpm; strip tags first so markup doesn't inflate it. */
function readingTime(html: string): number {
  if (!html) return 1;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const words = text ? text.split(' ').length : 0;
  return Math.max(1, Math.ceil(words / 220));
}

/** Mirrors CMS_CONTENT_TYPES in the backend; unknown keys title-case reasonably. */
function ctypeLabel(ctype: string): string {
  const map: Record<string, string> = {
    page: 'Page', article: 'Article', blog: 'Blog', news: 'News', faq: 'FAQ',
    legal: 'Legal', help: 'Help', announcement: 'Announcement', category: 'Category',
  };
  return map[ctype] || ctype.charAt(0).toUpperCase() + ctype.slice(1);
}

function formatAuthors(authors: string[] | null, fallback: string | null): string | null {
  const names = authors?.length ? authors : fallback ? [fallback] : null;
  if (!names?.length) return null;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * Structured (category) pages ship their own <section> layout; everything else
 * gets the readable prose layout so it doesn't render as a wall of HTML.
 */
function shouldUseStructuredLayout(ctype: string, html: string): boolean {
  if (ctype === 'category') return true;
  if (!html) return false;
  return /^<section\b/i.test(html.trimStart().slice(0, 200));
}

// ── Authors byline ───────────────────────────────────────────────────────────

const AuthorsByline: React.FC<{ authors: string[] | null; author: string | null }> = ({ authors, author }) => {
  const formatted = formatAuthors(authors, author);
  if (!formatted) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
      <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      <span>By {formatted}</span>
    </span>
  );
};

// ── Loading skeleton (ported from ff-ui ContentSkeleton) ─────────────────────

const ContentSkeleton: React.FC = () => (
  <div className="bg-white">
    <div className="border-b border-gray-100 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center animate-pulse">
        <div className="h-3 w-40 bg-gray-100 rounded" />
        <div className="ml-auto flex gap-4">
          <div className="h-3 w-16 bg-gray-100 rounded" />
          <div className="h-3 w-20 bg-gray-100 rounded" />
        </div>
      </div>
    </div>
    <div className="animate-pulse">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16">
        <div className="w-full aspect-[2.4/1] bg-gray-100 rounded-2xl mb-10" />
        <div className="h-10 w-3/4 bg-gray-200/60 rounded-lg mb-4" />
        <div className="h-5 w-full bg-gray-100/60 rounded mb-2" />
        <div className="h-5 w-2/3 bg-gray-100/40 rounded mb-6" />
        <div className="flex items-center gap-2 mb-10">
          <div className="w-4 h-4 bg-gray-100 rounded-full" />
          <div className="h-3 w-32 bg-gray-100 rounded" />
        </div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-50 rounded w-full" />
          <div className="h-4 bg-gray-50 rounded w-5/6" />
          <div className="h-4 bg-gray-50 rounded w-4/5" />
          <div className="h-4 bg-gray-50 rounded w-full" />
          <div className="h-4 bg-gray-50 rounded w-3/4" />
        </div>
      </div>
    </div>
  </div>
);

// ── Error / not-found state (ported from ff-ui ContentError) ─────────────────

const ContentError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center px-4 pt-32 pb-20">
    <div className="w-20 h-20 mb-8 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <svg className="w-9 h-9 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    </div>
    <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">Page not found</h1>
    <p className="text-gray-500 mb-10 text-center max-w-xs leading-relaxed">
      {message || "This page doesn't exist or may have been moved."}
    </p>
    <button
      onClick={onRetry}
      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 transition-all"
    >
      <RefreshCw className="w-4 h-4" />
      Retry
    </button>
  </div>
);

// ── Admin preview toolbar (replaces the public Header/Footer) ────────────────

const STATUS_STYLES: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-gray-100 text-gray-600',
  inactive: 'bg-gray-100 text-gray-600',
  expired: 'bg-amber-100 text-amber-700',
  deleted: 'bg-rose-100 text-rose-700',
};

const PreviewToolbar: React.FC<{
  cmsoid: number | null;
  page: CMSObject | null;
  onBack: () => void;
  onEdit: () => void;
}> = ({ cmsoid, page, onBack, onEdit }) => {
  const status = page ? deriveCMSStatus(page) : null;
  return (
    <div className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-indigo-700">
          Preview
        </span>
        {status && (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'}`}>
            {status}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {page?.slug && (
            <a
              href={publicContentUrl(page.slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ExternalLink className="h-4 w-4" />
              Open live
            </a>
          )}
          {cmsoid != null && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const CMSPagePreviewPage = () => {
  const navigate = useNavigate();
  const { cmsoid } = useParams<{ cmsoid?: string }>();
  const numericCmsoid = cmsoid && cmsoid !== 'new' ? Number(cmsoid) : null;

  // include_relations=false — the preview only renders the object itself + its
  // Assets (both eager-loaded regardless of this flag). Requesting relations
  // triggers the organization/entity/children joins that fail upstream, which
  // is what broke the fetch; the editor already loads with `false` for the same
  // reason. This mirrors ff-ui's public content fetch, which never asks for them.
  const { data: existing, isLoading, isError, error, refetch } = useCMSDetailQuery(numericCmsoid, false);
  const [visible, setVisible] = useState(false);

  // Decode plain-text fields (title/summary/author) exactly like the public page;
  // `content` stays untouched — it's server-sanitised HTML for dangerouslySetInnerHTML.
  const cmsData = useMemo<CMSObject | null>(() => {
    if (!existing) return null;
    const authors = Array.isArray(existing.authors) ? (existing.authors as unknown[]).map(String) : null;
    return {
      ...existing,
      title: decodeHtmlEntities(existing.title),
      summary: existing.summary ? decodeHtmlEntities(existing.summary) : existing.summary,
      author: existing.author ? decodeHtmlEntities(existing.author) : null,
      authors: decodeHtmlEntitiesArray(authors),
    };
  }, [existing]);

  const goToEditor = () =>
    navigate(numericCmsoid != null ? `/admin/content/pages/${numericCmsoid}` : '/admin/content/pages');

  useEffect(() => {
    if (cmsData) requestAnimationFrame(() => setVisible(true));
  }, [cmsData]);

  useEffect(() => {
    if (cmsData?.title) document.title = `Preview · ${cmsData.title}`;
  }, [cmsData?.title]);

  const assets = cmsData?.Assets ?? [];
  const minutes = useMemo(() => (cmsData ? readingTime(cmsData.content) : 0), [cmsData]);
  const useStructuredLayout = useMemo(
    () => (cmsData ? shouldUseStructuredLayout(cmsData.ctype, cmsData.content) : false),
    [cmsData],
  );

  const authorsList = (cmsData?.authors as string[] | null) ?? null;

  return (
    <div className="min-h-screen bg-white">
      <PreviewToolbar cmsoid={numericCmsoid} page={cmsData} onBack={() => navigate(-1)} onEdit={goToEditor} />

      {isLoading ? (
        <ContentSkeleton />
      ) : isError || !cmsData ? (
        <ContentError message={(error as any)?.message ?? ''} onRetry={refetch} />
      ) : (
        <>
          {/* Meta bar */}
          <div className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-14 z-30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-11 flex items-center text-[13px]">
              <nav className="flex items-center gap-1 text-gray-500 min-w-0">
                <button onClick={goToEditor} className="hover:text-gray-700 transition-colors flex-shrink-0">
                  Pages
                </button>
                <ChevronRight className="w-3 h-3 flex-shrink-0 text-gray-300" />
                <span className="text-gray-800 font-medium truncate">{cmsData.title}</span>
              </nav>
              <div className="ml-auto flex items-center gap-4 text-gray-500 flex-shrink-0">
                <span className="hidden sm:inline-flex items-center gap-1 uppercase tracking-wider text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                  {ctypeLabel(cmsData.ctype)}
                </span>
                {cmsData.date_published && (
                  <span className="hidden sm:inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(cmsData.date_published)}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {minutes} min
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <main className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {useStructuredLayout ? (
              <>
                {assets.length > 0 && (
                  <div className="max-w-7xl mx-auto">
                    <CMSMediaSlider assets={assets as any} title={cmsData.title} />
                  </div>
                )}

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 sm:pt-8 sm:pb-5">
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider text-gray-600 bg-gray-100 mr-3">
                      {ctypeLabel(cmsData.ctype)}
                    </span>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{cmsData.title}</h1>
                  </div>
                  {cmsData.summary && (
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-3xl">{cmsData.summary}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                    <AuthorsByline authors={authorsList} author={cmsData.author ?? null} />
                    {cmsData.date_modified && cmsData.date_modified !== cmsData.date_published && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="uppercase tracking-wider text-gray-500">Updated {formatDate(cmsData.date_modified)}</span>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className="cms-page-content [&_section]:scroll-mt-20 [&_img]:max-w-full"
                  dangerouslySetInnerHTML={{ __html: cmsData.content }}
                />
              </>
            ) : (
              <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
                {assets.length > 0 && (
                  <div className="mb-10 sm:mb-12 -mx-4 sm:-mx-6 lg:-mx-8">
                    <CMSMediaSlider assets={assets as any} title={cmsData.title} />
                  </div>
                )}

                <header className="mb-10 sm:mb-12">
                  <div className="flex flex-wrap items-center gap-2 mb-4 text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">{ctypeLabel(cmsData.ctype)}</span>
                    {cmsData.country_iso2 && (
                      <span title="Country-specific variant" className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                        {cmsData.country_iso2}
                      </span>
                    )}
                  </div>

                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 leading-[1.15] mb-3">
                    {cmsData.title}
                  </h1>
                  {cmsData.summary && (
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed">{cmsData.summary}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-4 text-xs text-gray-500">
                    <AuthorsByline authors={authorsList} author={cmsData.author ?? null} />
                    {cmsData.date_published && (
                      <>
                        {(authorsList?.length || cmsData.author) && <span className="text-gray-300">·</span>}
                        <span className="uppercase tracking-wider text-gray-500">Published {formatDate(cmsData.date_published)}</span>
                      </>
                    )}
                    {cmsData.date_modified && cmsData.date_modified !== cmsData.date_published && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="uppercase tracking-wider text-gray-500">Updated {formatDate(cmsData.date_modified)}</span>
                      </>
                    )}
                  </div>
                </header>

                <div
                  /* Body HTML is server-sanitised (bleach allowlist in ff-api model/cms.py).
                     `.cms-prose` reproduces the public page's Tailwind Typography styling. */
                  className="cms-prose max-w-none [&_img]:w-full [&_table]:w-full [&_pre]:max-w-none"
                  dangerouslySetInnerHTML={{ __html: cmsData.content }}
                />
              </article>
            )}
          </main>
        </>
      )}
    </div>
  );
};

export default CMSPagePreviewPage;
